// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// models
import type { ICreateGroupReq, IUpdateGroupReq } from "@/lib/backend/batch_operations.generated.types";
import type { ILineGroupData } from "@/lib/backend/manufacturing2.generated.types";
// Note: line_group tag is handled independently
import { BATCHOP_GROUP_TAGS, BATCHOP_GROUP_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import * as BusinessLogic from "@/lib/businessLogic";

// subcomponents
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Toggle from "react-toggle";
import Select from "react-select";
import { translate } from "@/modules/shared/IntlProviderWrapper";

// backend
import { getBackend } from "@/lib/backend/backend2";
import {
    Columns,
    mapDataToCategories,
    mapDataToCustomTags,
    onChangeUpdateTags,
    updateTagsConfig,
    type TagsConfig,
    type TagUpdateValue,
} from "@/modules/shared/ui/ReactTableTags";
import TagsTables from "@/modules/shared/ui/TagsTables";

import { BATCHOP_GROUP_TAGS as MAN_BATCHOP_GROUP_TAGS } from "@/lib/ManufacturingTagsEditor.generated";
import UserDocumentationLink from "@/modules/shared/UserDocumentationLink";
import type { ITags } from "@/lib/models";

import img_banner from "@/img/banner.png";

// type definitions
type ITagDisplay = {
    name: string;
    id: string;
};

type ISelectOption = {
    value: string;
    label: string;
};

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};
export type State = {
    uuid: string;
    tags: ITags;
    title: string;
    line_group: ISelectOption;
    line_groups: ILineGroupData[];
    message: string;
    componentTitle: string;
    error: string | null;
    warning: string;
};

/**
 * Form to create new line.
 */
export default class BatchopGroupCreate extends Component<Props, State> {
    state: State = {
        line_group: { value: "", label: "" },
        line_groups: [],
        uuid: "",
        title: "",
        tags: {},
        message: "",
        componentTitle: translate("Manufacturing.BatchopGroups.add_new_batchop_group", "Add New Batchop Group"),
        error: "",
        warning: "",
    };

    async componentDidMount() {
        const { line_groups } = await getBackend().manufacturing.getLineGroups({});
        this.setState({ line_groups });
    }

    /**
     * Basic form validation.
     */
    validateShallow() {
        return this.state.title.length > 0;
    }

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value: string | boolean = target.value;
        const name = target.name;

        // hack for toggle element
        if (Object.keys(BATCHOP_GROUP_TAGS).includes(name)) {
            value = (target as HTMLInputElement).checked;
        }

        // handle line group
        if (name === "line_group") {
            let tags = this.state.tags || {};
            tags.line_group = String(value);
            this.setState({ tags: tags });
        } else {
            // update new datasource placeholder
            if (Object.prototype.hasOwnProperty.call(this.state, name)) {
                this.setState(prevState => ({
                    ...prevState,
                    [name]: value,
                }));
            }
        }

        // handle tags
        if (Object.keys(BATCHOP_GROUP_TAGS).includes(name)) {
            let tags = this.state.tags || {};
            tags[name] = value.toString();
            this.setState({ tags });
        }
    };

    /**
     * Handling multi-select change.
     */
    handleMultiSelectChange = (data: ISelectOption) => {
        this.setState({ line_group: data });
        let tags = this.state.tags || {};
        tags.line_group = data.value;
    };

    /**
     * Handling submission of the form. Basic checks and saves with error handling.
     */
    handleSubmit = async () => {
        this.setState({ error: null });
        try {
            if (this.validateShallow()) {
                let uuid = this.props.match.params.uuid;

                // insert a new object or update
                if (this.state.uuid === "") {
                    let lg: ICreateGroupReq = {
                        title: this.state.title,
                        tags: this.state.tags,
                    };
                    const response = await getBackend().batchOperations.createGroup(lg);
                    uuid = response.uuid;
                } else {
                    // update current object
                    let lg: IUpdateGroupReq = {
                        group_id: this.state.uuid,
                        title: this.state.title,
                        tags: this.state.tags,
                    };
                    await getBackend().batchOperations.updateGroup(lg);
                    // force refresh cache to get new batchop group tags
                    await BusinessLogic.forceManufacturingCacheRefresh();
                }

                this.props.history.push(`/digital-twin/resources/batchop_groups/${uuid}`);
            } else {
                this.setState({
                    warning: translate(
                        "Manufacturing.LineGroups.cannot_submit_missing_values",
                        "Cannot submit a form with missing values"
                    ),
                });
            }
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    };

    renderTagField(tag: ITagDisplay) {
        return (
            <div className="form-group" key={tag.id}>
                <label>{tag.name}</label>
                <br />
                <Toggle name={tag.id} checked={this.state[tag.id]} onChange={this.handleInputChange} />
            </div>
        );
    }

    onGuiChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    renderReactTable() {
        const tagsConfig = updateTagsConfig(MAN_BATCHOP_GROUP_TAGS as TagsConfig);
        const tagCategories = mapDataToCategories(this.state.tags, tagsConfig, BATCHOP_GROUP_TAGS_ACCESS);
        return (
            <>
                <div className="text-right mb-3">
                    <UserDocumentationLink link_key={"batchop_groups"} />
                </div>
                <TagsTables
                    categories={tagCategories}
                    columns={Columns}
                    onChangeCell={this.onGuiChangeCell}
                    customTags={mapDataToCustomTags(this.state.tags, tagsConfig)}
                />
            </>
        );
    }

    render() {
        const tags: ITagDisplay[] = [];

        // handle line group options for select component
        let line_group_options: ISelectOption[] = this.state.line_groups.map(lg => {
            return { value: lg.uuid, label: `${lg.title}` };
        });

        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <h2 className="mb-4">{this.state.componentTitle}</h2>
                                    <ErrorComponent msg={this.state.error} type="error" />
                                    <ErrorComponent msg={this.state.warning} type="warning" />
                                    <div className="tab-content">
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.title" defaultMessage="Title" />:
                                            </label>
                                            <input
                                                name="title"
                                                type="text"
                                                className="form-control"
                                                value={this.state.title}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.linegroup" defaultMessage="Line group" />:
                                            </label>
                                            <Select
                                                name="line_group"
                                                value={this.state.line_group}
                                                options={line_group_options}
                                                onChange={this.handleMultiSelectChange}
                                            />
                                        </div>
                                    </div>
                                    {this.renderReactTable()}
                                    {tags.map(tag => this.renderTagField(tag))}
                                    <div className="button-list button-list-entity-edit order-last ml-auto">
                                        <button className="btn btn-primary" onClick={this.handleSubmit}>
                                            <FormattedMessage id="common.save" defaultMessage="Save" />
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => this.props.history.goBack()}
                                        >
                                            <FormattedMessage id="common.back" defaultMessage="Back" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
